import {
  Button,
  PlainButton,
  TextInput,
  TextAreaInput,
  DataLoadingWrapper,
  MultiSectionLayout,
  HeaderBreadcrumbs,
} from "@jugl-web/ui-kit";
import {
  AgentInstructionOptionId,
  useRestApiProvider,
  type AgentConfigData,
  type AgentTool,
  type CustomTemplateData,
} from "@jugl-web/rest-api";
import { useTranslations } from "@jugl-web/utils";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { CXAutomationDrawer } from "@web-src/modules/cx/pages/CXActionsPage/components/CXAutomationDrawer/CXAutomationDrawer";
import {
  CUSTOM_PROMPT_ID,
  isInstructionOptionId,
  normalizeInstructionItems,
  upsertInstructionItem,
} from "@web-src/modules/cx/pages/CXAIAgentsPage/components/SetupAndTestTabContent/components/SetupAndTestTabMenu/components/AgentInstructionsPanel/utils";
import React, { useCallback, useEffect, useState } from "react";
import { GenericToolsSection } from "./components/GenericToolsSection";
import { IntegrationToolsSection } from "./components/IntegrationToolsSection";

export const CXActionsFormPage: React.FC = () => {
  const { t } = useTranslations();
  const { navigateToPage } = useNavigation();
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();

  const {
    data: agentConfig,
    isLoading: isAgentConfigLoading,
    isError: isAgentConfigError,
    refetch: refetchAgentConfig,
  } = integrationsApi.useGetAiAgentConfigQuery({
    entityId,
    meta: { silent: true },
  });
  const [updateConfig, { isLoading: isUpdating }] =
    integrationsApi.useUpdateAgentConfigMutation();
  const { data: allConnections } = integrationsApi.useGetConnectionsQuery({
    entityId,
  });
  const connections = allConnections?.data ?? [];

  const [businessName, setBusinessName] = useState("");
  const [instructions, setInstructions] = useState("");
  const [genericTools, setGenericTools] = useState<string[]>([]);
  const [integrationTools, setIntegrationTools] = useState<AgentTool[]>([]);
  const [customAutomations, setCustomAutomations] = useState<
    CustomTemplateData[]
  >([]);
  const [customDialogIdx, setCustomDialogIdx] = useState<number | null>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!agentConfig || initialized) {
      return;
    }
    const cfg = agentConfig.config;
    const normalizedInstructions = normalizeInstructionItems(
      cfg.saved_instructions
    );
    const isSelectedOptionValid = isInstructionOptionId(
      cfg.selected_instruction_option
    );
    const selectedOption = isSelectedOptionValid
      ? cfg.selected_instruction_option
      : CUSTOM_PROMPT_ID;
    const selectedFromList = normalizedInstructions.find(
      (item) => item.name === selectedOption
    );
    const selectedInstruction = normalizedInstructions[0] ?? {
      name: CUSTOM_PROMPT_ID,
      value: "",
    };
    const resolvedInstructionValue = isSelectedOptionValid
      ? (selectedFromList ?? selectedInstruction).value || ""
      : cfg.instructions || "";

    setBusinessName(cfg.business_name || "");
    setInstructions(resolvedInstructionValue);

    const generic = cfg.tools?.find((tool) => tool.pack === "generic");
    setGenericTools(generic?.tools ?? []);

    setIntegrationTools(
      cfg.tools?.filter(
        (tool) =>
          tool.pack !== "generic" &&
          tool.pack !== "custom" &&
          !!tool.connection_id
      ) ?? []
    );

    setCustomAutomations(
      cfg.tools?.find((tool) => tool.pack === "custom")?.custom_template_data ??
        []
    );

    setInitialized(true);
  }, [agentConfig, initialized]);

  const buildToolsPayload = useCallback((): AgentTool[] => {
    const tools: AgentTool[] = [];

    if (genericTools.length > 0) {
      tools.push({ pack: "generic", tools: genericTools, params: {} });
    }

    tools.push(
      ...integrationTools.map((tool) => ({
        ...tool,
        params: tool.params ?? {},
      }))
    );

    if (customAutomations.length > 0) {
      tools.push({
        pack: "custom",
        tools: null,
        custom_template_data: customAutomations,
      });
    }

    return tools;
  }, [genericTools, integrationTools, customAutomations]);

  const handleSave = useCallback(async () => {
    if (!agentConfig) {
      return;
    }

    // Persist whichever option ID was already on the server. This page has no
    // UI to change it — the value is owned by the Setup & Test panel — so we
    // round-trip it untouched (falling back to CUSTOM_PROMPT_ID for safety).
    const savedOption = agentConfig.config.selected_instruction_option;
    const selectedOption: AgentInstructionOptionId = isInstructionOptionId(
      savedOption
    )
      ? savedOption
      : CUSTOM_PROMPT_ID;
    const trimmedBusinessName = businessName.trim();
    const trimmedInstructions = instructions.trim();
    const nextSavedInstructions = upsertInstructionItem(
      agentConfig.config.saved_instructions,
      selectedOption,
      trimmedInstructions
    );

    const config: AgentConfigData = {
      ...agentConfig.config,
      business_name: trimmedBusinessName,
      instructions: trimmedInstructions,
      selected_instruction_option: selectedOption,
      saved_instructions: nextSavedInstructions,
      tools: buildToolsPayload(),
    };

    try {
      await updateConfig({
        entityId,
        config,
        meta: {
          successMessage: t({
            id: "cx-agent-setup.save-success",
            defaultMessage: "Agent configuration saved",
          }),
          getErrorMessage: () =>
            t({
              id: "cx-agent-setup.save-error",
              defaultMessage: "Failed to save configuration",
            }),
        },
      }).unwrap();
    } catch {
      // Error UI handled via meta + apiResultMiddleware
    }
  }, [
    agentConfig,
    businessName,
    instructions,
    buildToolsPayload,
    updateConfig,
    entityId,
    t,
  ]);

  const handleCustomToolSave = (data: CustomTemplateData) => {
    if (
      customDialogIdx !== null &&
      customDialogIdx < customAutomations.length
    ) {
      setCustomAutomations((prev) =>
        prev.map((item, i) => (i === customDialogIdx ? data : item))
      );
    } else {
      setCustomAutomations((prev) => [...prev, data]);
    }
    setCustomDialogIdx(null);
  };

  const removeCustomTool = (idx: number) =>
    setCustomAutomations((prev) => prev.filter((_, i) => i !== idx));

  const isSaving = isUpdating;

  return (
    <MultiSectionLayout
      header={
        <HeaderBreadcrumbs
          items={[
            {
              title: t({
                id: "cx-ai-agents-page.ai-agent",
                defaultMessage: "AI Agent",
              }),
              onClick: () =>
                navigateToPage("cxAIAgents", undefined, {
                  state: { defaultTab: "automate" },
                }),
            },
            {
              title: t({
                id: "cx-actions-form-page.agent-setup",
                defaultMessage: "Agent Setup",
              }),
            },
          ]}
        />
      }
    >
      <DataLoadingWrapper
        isLoading={isAgentConfigLoading}
        isError={isAgentConfigError}
        onRetry={refetchAgentConfig}
      >
        <div className="jugl__custom-scrollbar h-full overflow-y-auto">
          <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-10 p-12">
            <div className="flex flex-col gap-10 rounded-2xl bg-white p-8">
              <div className="flex flex-col gap-4">
                <span className="font-secondary font-medium text-dark">
                  {t({
                    id: "cx-agent-setup.business-info",
                    defaultMessage: "Business Info",
                  })}
                </span>
                <TextInput
                  labelPosition="border-overlap"
                  label={t({
                    id: "cx-agent-setup.business-name",
                    defaultMessage: "Business Name",
                  })}
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value.trimStart())}
                />
              </div>
              <div className="flex flex-col gap-4">
                <span className="font-secondary font-medium text-dark">
                  {t({
                    id: "cx-agent-setup.instructions",
                    defaultMessage: "Instructions",
                  })}
                </span>
                <TextAreaInput
                  label={t({
                    id: "cx-agent-setup.instructions-label",
                    defaultMessage: "Agent Instructions",
                  })}
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value.trimStart())}
                  classNames={{ inputClassName: "min-h-[160px]" }}
                />
              </div>
              <div className="flex flex-col gap-4">
                <span className="font-secondary font-medium text-dark">
                  {t({
                    id: "cx-agent-setup.generic-tools",
                    defaultMessage: "Generic Tools",
                  })}
                </span>
                <GenericToolsSection
                  selected={genericTools}
                  onChange={setGenericTools}
                />
              </div>
              <div className="flex flex-col gap-4">
                <span className="font-secondary font-medium text-dark">
                  {t({
                    id: "cx-agent-setup.integration-tools",
                    defaultMessage: "Integration Tools",
                  })}
                </span>
                <IntegrationToolsSection
                  connections={connections}
                  tools={integrationTools}
                  onChange={setIntegrationTools}
                />
              </div>
              <div className="flex flex-col gap-4">
                <span className="font-secondary font-medium text-dark">
                  {t({
                    id: "cx-agent-setup.custom-tools",
                    defaultMessage: "Custom Tools",
                  })}
                </span>
                <div className="flex flex-col gap-3">
                  {customAutomations?.map((automation, idx) => (
                    <div
                      key={+idx}
                      className="flex items-center justify-between rounded-xl border border-solid border-[#F2F2F2] px-4 py-3"
                    >
                      <div className="flex flex-col">
                        <span className="font-secondary text-sm font-medium text-[#383838]">
                          {automation.name?.replace(/_/g, " ") ||
                            `Custom Tool ${idx + 1}`}
                        </span>
                        <span className="text-xs text-dark-600">
                          {automation.method} {automation.url}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <PlainButton
                          className="font-secondary text-sm text-primary"
                          onClick={() => setCustomDialogIdx(idx)}
                        >
                          {t({ id: "common.edit", defaultMessage: "Edit" })}
                        </PlainButton>
                        <PlainButton
                          className="font-secondary text-sm text-tertiary-800"
                          onClick={() => removeCustomTool(idx)}
                        >
                          {t({ id: "common.remove", defaultMessage: "Remove" })}
                        </PlainButton>
                      </div>
                    </div>
                  ))}
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setCustomDialogIdx(customAutomations.length)}
                  >
                    {t({
                      id: "cx-agent-setup.add-custom-tool",
                      defaultMessage: "Add Custom Tool",
                    })}
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Button onClick={handleSave} isLoading={isSaving}>
                  {t({ id: "common.save", defaultMessage: "Save" })}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DataLoadingWrapper>
      {customDialogIdx !== null && (
        <CXAutomationDrawer
          isOpen
          onRequestClose={() => setCustomDialogIdx(null)}
          entityId={entityId}
          onSave={handleCustomToolSave}
          isSaving={isSaving}
          initialAutomation={
            customDialogIdx < customAutomations.length
              ? customAutomations[customDialogIdx]
              : undefined
          }
        />
      )}
    </MultiSectionLayout>
  );
};
